/**
 * Builds the single Markdown document shown in the job page's Job Description card.
 *
 * A posting reads as ONE document, so the job page renders one — the extraction
 * layer already organises `description` under `##` section headings, and separate
 * Requirements / Qualifications cards alongside it just fragmented the same
 * posting across three boxes.
 *
 * `requirements` and `qualifications` are folded in rather than dropped, because
 * for a MANUALLY created job an admin typed them into the form and the
 * description genuinely does not contain them. For a SCRAPED job they are always
 * a copy of the description, so everything here is de-duplicated against it: a
 * line the description already states is never printed twice, and a section that
 * has nothing left to add is not emitted at all.
 *
 * The de-duplication deliberately happens HERE rather than relying on the
 * fetcher's own guard. That guard only shapes rows written after it shipped; the
 * view has to hold the "no section appears twice" invariant for every row it is
 * ever handed, whatever wrote it.
 */

/** Fold to a shape that ignores casing, whitespace runs and quote styles. */
function comparable(value: string): string {
  return value
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Split a Markdown bullet list (or plain lines) into its individual entries. */
function toLines(markdown: string): string[] {
  return markdown
    .split('\n')
    .map((line) => line.replace(/^\s*[-*+]\s+/, '').trim())
    .filter(Boolean);
}

/** True when the description already carries a top-level section of this name. */
function hasHeading(description: string, title: string): boolean {
  const wanted = comparable(title);
  return description
    .split('\n')
    .some((line) => {
      const match = line.match(/^#{1,6}\s+(.*?)\s*$/);
      return match ? comparable(match[1]) === wanted : false;
    });
}

/**
 * Emit `## <title>` plus the lines the description does not already state, or
 * `null` when it states all of them. When the description already has a section
 * of the same name, the leftovers are labelled "Additional …" so the document
 * never shows the same heading twice.
 */
function extraSection(description: string, title: string, lines: string[]): string | null {
  const haystack = comparable(description);
  const leftover = lines.filter((line) => !haystack.includes(comparable(line)));
  if (!leftover.length) return null;
  const heading = hasHeading(description, title) ? `Additional ${title}` : title;
  return `## ${heading}\n\n${leftover.map((line) => `- ${line}`).join('\n')}`;
}

export function composeJobBody(job: {
  description?: string | null;
  requirements?: string | null;
  qualifications?: string[] | null;
}): string {
  const description = job.description?.trim() ?? '';
  const sections: string[] = [];
  if (description) sections.push(description);

  const requirements = toLines(job.requirements?.trim() ?? '');
  if (requirements.length) {
    const section = extraSection(description, 'Requirements', requirements);
    if (section) sections.push(section);
  }

  const qualifications = (job.qualifications ?? [])
    .map((q) => q?.trim())
    .filter((q): q is string => Boolean(q));
  if (qualifications.length) {
    const section = extraSection(description, 'Qualifications', qualifications);
    if (section) sections.push(section);
  }

  return sections.join('\n\n');
}
